import type { ExportRequest } from '@/services/dbConsole'
import type { Notification } from '@/store/notificationStore'

const DAY_MS = 24 * 60 * 60 * 1000

/** Export Vault, opened on one request: who asked, for what, how large (T-42/T-43). */
export function exportRequestUrl(requestId: string): string {
  return `/db-search?tab=exports&request=${encodeURIComponent(requestId)}`
}

/**
 * The notification-bell entries for Database Console exports, for one signed-in
 * person. Two kinds, both pointing at Export Vault:
 *
 * - a pending request that NAMES this person as the approver (high priority:
 *   nothing moves until they act); a request raised before approvers were
 *   named is not pushed at anyone;
 * - a request this person raised, once it has been approved (the download
 *   window is open) or denied, for a day.
 */
export function exportApprovalNotifications(
  requests: ExportRequest[],
  viewerEmail: string,
  now: Date = new Date(),
): Notification[] {
  const me = viewerEmail.trim().toLowerCase()
  if (!me) return []
  const out: Notification[] = []
  for (const request of requests) {
    const created = request.createdAt ?? now.toISOString()
    const who = request.requestedByName || request.requestedByEmail
    const files = request.includeAttachments ? ' with the attachment files' : ''
    if (request.status === 'pending' && (request.designatedApproverEmail ?? '').toLowerCase() === me) {
      out.push({
        id: `live-export-approval-${request.id}`,
        type: 'system',
        title: 'Export waiting for your approval',
        message: `${who} asked to export ${request.scope.length} table${request.scope.length === 1 ? '' : 's'}${files}: ${request.reason}`,
        timestamp: created,
        read: false,
        priority: 'high',
        actionUrl: exportRequestUrl(request.id),
        module: 'database',
        icon: 'ShieldCheck',
      })
    } else if (request.requestedByEmail.toLowerCase() === me) {
      const decidedAt = request.approvedAt ? new Date(request.approvedAt) : null
      const recent = decidedAt !== null && now.getTime() - decidedAt.getTime() < DAY_MS
      const approver = request.designatedApproverName || request.approvedByEmail || 'Your approver'
      if (request.status === 'approved' && !request.tokenExpired) {
        out.push({
          id: `live-export-approved-${request.id}`,
          type: 'system',
          title: 'Your export was approved',
          message: `${approver} approved it. Get the one-time download key from them and download within the window.`,
          timestamp: request.approvedAt ?? created,
          read: false,
          priority: 'high',
          actionUrl: exportRequestUrl(request.id),
          module: 'database',
          icon: 'ShieldCheck',
        })
      } else if (request.status === 'denied' && recent) {
        out.push({
          id: `live-export-denied-${request.id}`,
          type: 'system',
          title: 'Your export was denied',
          message: `${approver} denied it${request.decisionNote ? `: ${request.decisionNote}` : '.'}`,
          timestamp: request.approvedAt ?? created,
          read: false,
          priority: 'medium',
          actionUrl: exportRequestUrl(request.id),
          module: 'database',
          icon: 'ShieldCheck',
        })
      }
    }
  }
  return out
}
